import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import {
  ASSET_TYPE_OPTIONS, ASSET_LIFECYCLE_OPTIONS, ASSET_LIFECYCLE_META,
  FIELD_TYPE_META, parseJsonArray, toJsonArrayString,
} from './assetShared';

// Demirbaş Tanımı (asset model) detay — GET-by-id YOK; nav-state row ile, yoksa listeyi
// (take=500) çekip id ile bulunur. Sekmeler: Genel / Özellikler / Hedef. CRUD REST.
const API_BASE = '/api/asset-models';
const ATTR_API = '/api/asset-attribute-defs';

const EMPTY_FORM = {
  textId: '',
  code: '',
  name: '',
  assetType: 'COOLER',
  brand: '',
  isSerialTracked: false,
  monthlyTargetCases: '',
  selectedAttributeIds: [],
  lifecycleStatus: '1',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function rowToForm(row) {
  return {
    textId: pick(row, 'textId', 'TextId') || '',
    code: pick(row, 'code', 'Code') || '',
    name: pick(row, 'name', 'Name') || '',
    assetType: pick(row, 'assetType', 'AssetType') || 'COOLER',
    brand: pick(row, 'brand', 'Brand') || '',
    isSerialTracked: !!pick(row, 'isSerialTracked', 'IsSerialTracked'),
    monthlyTargetCases: pick(row, 'monthlyTargetCases', 'MonthlyTargetCases') ?? '',
    selectedAttributeIds: parseJsonArray(pick(row, 'selectedAttributeIdsJson', 'SelectedAttributeIdsJson')).map(String),
    lifecycleStatus: String(pick(row, 'lifecycleStatus', 'LifecycleStatus') ?? 1),
  };
}

export function AssetModelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Demirbaş Tanımı' : 'Demirbaş Tanımı Detayı', 'Demirbaş modeli — marka, seri takibi, özellikler ve cihaz hedefi.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [attributeDefs, setAttributeDefs] = useState([]);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const applyAudit = (r) => setAudit({
    createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'),
    createdDate: pick(r, 'createdDate', 'CreatedDate'),
    updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'),
    updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate'),
  });

  // Özellik chip'leri için tanımları çek.
  useEffect(() => {
    fetch(`${ATTR_API}?take=500`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (!payload) return;
        const data = unwrap(payload);
        const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
        setAttributeDefs(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(pick(stateRow, 'id', 'Id')) === String(id)) {
      setForm(rowToForm(stateRow));
      applyAudit(stateRow);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Demirbaş tanımı alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
      const row = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!row) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(row));
      applyAudit(row);
    } catch (err) {
      showToast('error', err.message || 'Demirbaş tanımı alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const toggleAttribute = (attrId) => setForm((c) => {
    const key = String(attrId);
    const has = c.selectedAttributeIds.includes(key);
    return { ...c, selectedAttributeIds: has ? c.selectedAttributeIds.filter((x) => x !== key) : [...c.selectedAttributeIds, key] };
  });

  const buildBody = () => ({
    textId: (form.textId || '').trim() || null,
    code: (form.code || '').trim(),
    name: (form.name || '').trim() || null,
    assetType: form.assetType,
    brand: (form.brand || '').trim() || null,
    isSerialTracked: !!form.isSerialTracked,
    monthlyTargetCases: form.monthlyTargetCases === '' ? 0 : Number(form.monthlyTargetCases),
    selectedAttributeIdsJson: toJsonArrayString(form.selectedAttributeIds),
    lifecycleStatus: Number(form.lifecycleStatus) || 0,
  });

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!String(form.code || '').trim()) { showToast('error', 'Kod zorunlu.'); setActiveTab('general'); return; }
    if (!form.assetType) { showToast('error', 'Demirbaş tipi zorunlu.'); setActiveTab('general'); return; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Demirbaş tanımı eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          setActiveTab('general');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Demirbaş tanımı oluşturuldu.');
        setTimeout(() => navigate('/asset-models'), 400);
        return;
      }
      showToast('success', 'Demirbaş tanımı güncellendi.');
      setTimeout(() => navigate('/asset-models'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => navigate('/asset-models/new', { state: { clone: { ...form, code: '', textId: '' } } });

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Demirbaş tanımı silindi.');
      setTimeout(() => navigate('/asset-models'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Demirbaş Tanımı' : (form.code || form.name || 'Demirbaş Tanımı Detayı');
  const stMeta = ASSET_LIFECYCLE_META[Number(form.lifecycleStatus)] || { label: '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={stMeta.tone}>{stMeta.label}</StatusBadge>;

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'box' },
    { key: 'attributes', label: `Özellikler${form.selectedAttributeIds.length ? ` (${form.selectedAttributeIds.length})` : ''}`, icon: 'tag' },
    { key: 'target', label: 'Hedef', icon: 'calendar' },
  ];

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/asset-models')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton label={saving ? 'Kaydediliyor…' : 'Kaydet'} icon="check" onClick={handleSave} disabled={saving}
          menu={[{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni kayıt aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]} />
      ) : (
        <SplitButton label={saving ? 'Kaydediliyor…' : 'Kaydet'} icon="check" onClick={handleSave} disabled={saving}
          menu={[{ label: 'Klonla', desc: 'Bu kaydı kopyalayıp yeni tanım aç', icon: 'copy', onClick: handleClone }]} />
      ))}
    </>
  );

  const radioBtn = (checked) => ({
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
    padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13,
    border: checked ? '1px solid var(--accent, #0073ea)' : '1px solid var(--border-color, #e5e7eb)',
    background: checked ? 'var(--accent-soft, rgba(0, 115, 234,0.12))' : 'var(--bg-card, #fff)',
    color: checked ? 'var(--accent, #0073ea)' : 'var(--text-secondary, #6b7280)',
  });

  return (
    <DetailShell
      crumb={[{ label: 'Demirbaş Tanımı', to: '/asset-models' }, { label: isNew ? 'Yeni Demirbaş Tanımı' : 'Demirbaş Tanımı Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'AssetModel', entityId: id }}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        {activeTab === 'general' && (
          <Section icon="box" title="Genel Bilgiler" subtitle="Kod, model, tip, marka ve seri takibi">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="code" label="Kod" req mono placeholder="örn. CLR-330" />
              <FieldText form={form} set={set} k="name" label="Model Adı" placeholder="örn. 330L Cam Kapı Soğutucu" />
              <FieldSelect form={form} set={set} k="assetType" label="Demirbaş Tipi" options={ASSET_TYPE_OPTIONS} req />
              <FieldText form={form} set={set} k="brand" label="Marka" />
              <FieldText form={form} set={set} k="textId" label="Kod (TextId)" />
              <FieldSelect form={form} set={set} k="lifecycleStatus" label="Durum" options={ASSET_LIFECYCLE_OPTIONS} req />
              <FieldCustom label="Seri Takibi" span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" style={radioBtn(!!form.isSerialTracked)} onClick={() => set('isSerialTracked', true)}>
                    <span>{form.isSerialTracked ? '●' : '○'}</span> Var
                  </button>
                  <button type="button" style={radioBtn(!form.isSerialTracked)} onClick={() => set('isSerialTracked', false)}>
                    <span>{!form.isSerialTracked ? '●' : '○'}</span> Yok
                  </button>
                </div>
              </FieldCustom>
            </Grid>
          </Section>
        )}

        {activeTab === 'attributes' && (
          <Section icon="tag" title="Özellikler" subtitle="Bu modele takılacak özellikleri (attribute) seçin">
            {attributeDefs.length === 0 ? (
              <p style={{ color: 'var(--text-secondary, #6b7280)', margin: 0 }}>
                Henüz özellik tanımı yok. Önce “Demirbaş Özellik Tanımı” ekranından özellik ekleyin.
              </p>
            ) : (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {attributeDefs.map((a) => {
                  const aid = String(pick(a, 'id', 'Id'));
                  const on = form.selectedAttributeIds.includes(aid);
                  const ft = pick(a, 'fieldType', 'FieldType');
                  const ftLabel = FIELD_TYPE_META[ft]?.label || ft || '';
                  return (
                    <button
                      key={aid}
                      type="button"
                      onClick={() => toggleAttribute(aid)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 13,
                        border: on ? '1px solid var(--accent, #0073ea)' : '1px solid var(--border-color, #e5e7eb)',
                        background: on ? 'var(--accent-soft, rgba(0, 115, 234,0.12))' : 'var(--bg-card, #fff)',
                        color: on ? 'var(--accent, #0073ea)' : 'var(--text-primary, #111827)',
                      }}
                    >
                      <span style={{ fontWeight: 800 }}>{on ? '✓' : '+'}</span>
                      <span style={{ fontWeight: 700 }}>{pick(a, 'name', 'Name') || '(isimsiz)'}</span>
                      {ftLabel && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>· {ftLabel}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </Section>
        )}

        {activeTab === 'target' && (
          <Section icon="calendar" title="Cihaz Hedefi" subtitle="Bu modele ait cihazlar için varsayılan aylık hedef (koli)">
            <Grid cols={2}>
              <FieldText form={form} set={set} k="monthlyTargetCases" label="Aylık Cihaz Hedefi (koli)" num placeholder="örn. 40" suffix="koli"
                hint="Envanterde cihaza özel hedef girilmezse bu değer kullanılır." />
            </Grid>
          </Section>
        )}
      </div>

      {deleteConfirm && (
        <SubModal title="Demirbaş Tanımı Sil" maxWidth={440} onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.code || form.name || title}”</strong> demirbaş tanımını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default AssetModelDetailPage;
